import { createEffect, createMemo, createSignal, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { type SquadDraft, type SquadTarget, squadPatch } from '../lib/squad-editor';
import { type NamedSquad, pickerRepos, type SquadWire } from '../lib/squads';
import { Field } from './Field';
import { Modal } from './Modal';
import { SquadRepoPicker } from './SquadRepoPicker';
import { Button, buttonClass } from './ui/Button';

// Create or edit one squad (#6780): its name and its repos. Presenter — the screen's
// editor (`lib/squad-editor.ts`) owns the write, and the name rule is the domain's,
// run by `lib/squads.ts` before any request; its refusal arrives here as `nameError`.
//
// Always MOUNTED, driven by `target`: unmounting on close would cut the dialog's exit
// animation, so the fields re-seed from the target instead.

export function SquadEditModal(props: {
  readonly target: SquadTarget;
  /** The whole `GET /v1/squads` read: the picker's only source. */
  readonly rows: readonly SquadWire[];
  readonly pending: boolean;
  readonly nameError: string | null;
  readonly formError: string | null;
  readonly onSave: (draft: SquadDraft) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const [name, setName] = createSignal('');
  const [selected, setSelected] = createSignal<ReadonlySet<string>>(new Set());
  const squad = (): NamedSquad | null =>
    props.target === null || props.target === 'new' ? null : props.target;

  // Seeding local field state from an incoming prop is a legitimate effect: it
  // initializes on open, it does not mirror derived state.
  createEffect(
    on(
      () => props.target,
      () => {
        setName(squad()?.name ?? '');
        setSelected(new Set(squad()?.repos ?? []));
      },
    ),
  );

  const repos = createMemo(() => pickerRepos(props.rows, squad()?.id ?? null));
  const draft = (): SquadDraft => ({ name: name(), repos: [...selected()] });
  // An edit that changes nothing has no PATCH to send (the route needs a half).
  const unchanged = (): boolean => {
    const s = squad();
    return s !== null && squadPatch(s, draft()) === null;
  };

  const toggle = (fullName: string): void => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(fullName)) next.delete(fullName);
      else next.add(fullName);
      return next;
    });
  };

  const submit = (e: Event): void => {
    e.preventDefault();
    if (props.pending || unchanged()) return;
    props.onSave(draft());
  };

  const title = (): string => {
    const s = squad();
    return s === null
      ? t('squads.modal_new_title')
      : t('squads.modal_edit_title', { name: s.name });
  };

  return (
    <Modal open={props.target !== null} size="lg" title={title()} onClose={props.onClose}>
      <form class="squad-edit__form" onSubmit={submit}>
        <div class="field-group">
          <Field
            label={t('squads.name_label')}
            id="squad-name"
            error={props.nameError ?? undefined}
          >
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="text"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
              />
            )}
          </Field>
          <SquadRepoPicker repos={repos()} selected={selected()} onToggle={toggle} />
        </div>

        <Show when={props.formError}>
          {(message) => (
            <p class="field__error" role="alert">
              {message()}
            </p>
          )}
        </Show>

        <div class="dialog__footer">
          <Button variant="outline" disabled={props.pending} onClick={props.onClose}>
            {t('squads.cancel')}
          </Button>
          <button type="submit" class={buttonClass()} disabled={props.pending || unchanged()}>
            {props.pending
              ? t('squads.saving')
              : t(squad() === null ? 'squads.create' : 'squads.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
